import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Heart, ShoppingCart, User, Menu, X, Globe, ChevronDown, Package } from "lucide-react";
import Logo from "@/components/common/Logo";
import SearchBar from "@/components/common/SearchBar";
import { getCategories } from "@/services/catalog";
import { useI18n } from "@/i18n/I18nProvider";
import { useApp } from "@/context/AppContext";

function Selector({ value, options, onChange, icon: Icon, testid }) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 cursor-pointer appearance-none rounded-full border border-border bg-transparent ps-8 pe-6 text-sm outline-none hover:border-ink"
        data-testid={testid}
      >
        {options.map((o) => <option key={o.code} value={o.code}>{o.label || o.code}</option>)}
      </select>
      {Icon && <Icon size={14} className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" />}
      <ChevronDown size={13} className="pointer-events-none absolute end-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
    </div>
  );
}

export default function Header() {
  const { t, lang, setLang, currency, setCurrency, languages, currencies } = useI18n();
  const { cartCount, wishlist, user } = useApp();
  const [menuOpen, setMenuOpen] = useState(false);
  const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: getCategories });

  const accountLink = user ? "/account" : "/auth";

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/90 backdrop-blur-md" data-testid="site-header">
      <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-3">
        <button className="lg:hidden" onClick={() => setMenuOpen(true)} aria-label="menu" data-testid="mobile-menu-btn">
          <Menu />
        </button>
        <Logo />
        <div className="hidden flex-1 md:block">
          <SearchBar />
        </div>
        <div className="ms-auto flex items-center gap-2 sm:gap-3">
          <div className="hidden items-center gap-2 lg:flex">
            <Selector value={lang} options={languages.map((l) => ({ code: l.code, label: l.label }))} onChange={setLang} icon={Globe} testid="language-select" />
            <Selector value={currency} options={currencies.map((c) => ({ code: c.code, label: `${c.code} ${c.symbol}` }))} onChange={setCurrency} testid="currency-select" />
          </div>
          <Link to={accountLink} className="hidden flex-col items-start leading-tight sm:flex" data-testid="account-link">
            <span className="text-[11px] text-muted-foreground">{user ? t("welcome_back") : t("hello_sign_in")}</span>
            <span className="flex items-center gap-1 text-sm font-semibold text-ink"><User size={14} /> {user ? user.name : t("account")}</span>
          </Link>
          <Link to="/wishlist" className="relative grid h-10 w-10 place-items-center rounded-full hover:bg-secondary" data-testid="wishlist-link" aria-label={t("wishlist")}>
            <Heart size={20} />
            {wishlist.length > 0 && <span className="absolute -end-0.5 -top-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-brand px-1 text-[10px] font-bold text-brand-foreground">{wishlist.length}</span>}
          </Link>
          <Link to="/cart" className="relative grid h-10 w-10 place-items-center rounded-full hover:bg-secondary" data-testid="cart-link" aria-label={t("cart")}>
            <ShoppingCart size={20} />
            {cartCount > 0 && <span className="absolute -end-0.5 -top-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-brand px-1 text-[10px] font-bold text-brand-foreground">{cartCount}</span>}
          </Link>
        </div>
      </div>

      <div className="px-4 pb-3 md:hidden">
        <SearchBar />
      </div>

      <nav className="hidden border-t border-border lg:block" data-testid="category-nav">
        <div className="mx-auto flex max-w-7xl items-center gap-1 px-4">
          <NavLink to="/shop" className="flex items-center gap-1.5 py-2.5 pe-4 text-sm font-semibold text-ink">
            <Menu size={16} /> {t("all_categories")}
          </NavLink>
          <div className="flex items-center gap-1 overflow-x-auto no-scrollbar">
            {categories.map((c) => (
              <NavLink
                key={c.id}
                to={`/category/${c.slug}`}
                className={({ isActive }) => `whitespace-nowrap rounded-full px-3 py-1.5 text-sm transition hover:bg-secondary ${isActive ? "text-brand" : "text-foreground"}`}
              >
                {c.name}
              </NavLink>
            ))}
          </div>
          <Link to="/track" className="ms-auto flex items-center gap-1.5 py-2.5 text-sm font-medium text-muted-foreground hover:text-brand">
            <Package size={15} /> {t("track_order")}
          </Link>
        </div>
      </nav>

      {menuOpen && (
        <div className="fixed inset-0 z-[70] lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMenuOpen(false)} />
          <div className="absolute inset-y-0 start-0 w-80 max-w-[85%] overflow-y-auto bg-background p-5" data-testid="mobile-menu">
            <div className="mb-6 flex items-center justify-between">
              <Logo />
              <button onClick={() => setMenuOpen(false)}><X /></button>
            </div>
            <div className="mb-4 flex gap-2">
              <Selector value={lang} options={languages.map((l) => ({ code: l.code, label: l.label }))} onChange={setLang} icon={Globe} testid="language-select-mobile" />
              <Selector value={currency} options={currencies.map((c) => ({ code: c.code, label: `${c.code} ${c.symbol}` }))} onChange={setCurrency} testid="currency-select-mobile" />
            </div>
            <Link to={accountLink} onClick={() => setMenuOpen(false)} className="mb-4 flex items-center gap-2 rounded-xl bg-secondary p-3 text-sm font-semibold"><User size={18} /> {user ? user.name : t("hello_sign_in")}</Link>
            <div className="mb-2 text-xs font-bold uppercase text-muted-foreground">{t("all_categories")}</div>
            <div className="space-y-1">
              {categories.map((c) => (
                <Link key={c.id} to={`/category/${c.slug}`} onClick={() => setMenuOpen(false)} className="block rounded-lg px-3 py-2 text-sm hover:bg-secondary">{c.name}</Link>
              ))}
            </div>
            <div className="mt-4 space-y-1 border-t border-border pt-4">
              <Link to="/track" onClick={() => setMenuOpen(false)} className="block rounded-lg px-3 py-2 text-sm hover:bg-secondary">{t("track_order")}</Link>
              <Link to="/wishlist" onClick={() => setMenuOpen(false)} className="block rounded-lg px-3 py-2 text-sm hover:bg-secondary">{t("wishlist")}</Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
